import { App } from "@capacitor/app";
import type { PluginListenerHandle } from "@capacitor/core";
import { parseDeepLink, type DeepLinkIntent } from "./deep-link";

export interface AppLifecycleHandlers {
  onDeepLink(intent: DeepLinkIntent): void;
  onResume(): void | Promise<void>;
}

export interface AppLifecyclePort {
  addListener: typeof App.addListener;
  getLaunchUrl: typeof App.getLaunchUrl;
}

async function removeListeners(listeners: PluginListenerHandle[], context: string): Promise<void> {
  const results = await Promise.allSettled(listeners.map((listener) => listener.remove()));
  const failures = results
    .filter((result): result is PromiseRejectedResult => result.status === "rejected")
    .map((result) => result.reason);
  if (failures.length) throw new AggregateError(failures, context);
}

export async function startAppLifecycle(
  handlers: AppLifecycleHandlers,
  app: AppLifecyclePort = App,
): Promise<() => Promise<void>> {
  const listeners: PluginListenerHandle[] = [];
  try {
    listeners.push(
      await app.addListener("appUrlOpen", ({ url }) => {
        const intent = parseDeepLink(url);
        if (intent) handlers.onDeepLink(intent);
      }),
    );
    listeners.push(
      await app.addListener("resume", () => {
        void handlers.onResume();
      }),
    );

    const launch = await app.getLaunchUrl();
    if (launch?.url) {
      const intent = parseDeepLink(launch.url);
      if (intent) handlers.onDeepLink(intent);
    }
  } catch (error) {
    try {
      await removeListeners(listeners, "app lifecycle rollback failed");
    } catch (rollbackError) {
      throw new AggregateError([error, rollbackError], "app lifecycle initialization and rollback failed");
    }
    throw error;
  }

  return async () => {
    await removeListeners(listeners, "app lifecycle shutdown failed");
  };
}
